import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import createSagaMiddleware from 'redux-saga';
import { applyMiddleware, combineReducers, createStore } from 'redux';

import { accountReducer } from 'CorePackage/AccountModule/reducers/accountReducer';
import { headerReducer } from 'CorePackage/HeaderModule/reducers/headerReducer';
import { userInfoReducer } from 'PersonalAccountPackage/UserInfoModule/reducers/userInfoReducer';
import { analyticsReducer } from 'AnalyticsPackage/MainModule/reducers/analyticsReducer';
import { catalogsReducer } from 'CorePackage/CatalogsModule/reducers/catalogsReducer';
import { dictionariesReducer } from 'CorePackage/DictionariesModule/reducers/dictionariesReducer';
import { noticeReducer } from 'CorePackage/NotificationModule/reducers/noticeReducer';
import { registryReducer } from 'CorePackage/RegistryModule/reducers/registryReducer';
import { knmReducer } from 'KnmPackage/MainModule/reducers/knmReducer';
import { planListReducer } from 'PlanListPackage/MainModule/reducers/planListReducer';
import { planListFilterReducer } from 'PlanListPackage/PlanListPanelModule/reducers/planListFilterReducer';
import { planReducer } from 'PlanPackage/MainModule/reducers/planReducer';
import { knmListByPlanReducer } from 'KnmListByPlanPackage/MainModule/reducers/knmListByPlanReducer';
import { smevMonitoringReducer } from 'SmevMonitoringPackage/MainModule/reducers/smevMonitoringReducer';
import { exportReducer } from 'ExportPackage/MainModule/reducers/exportReducer';
import { knmListReducer } from 'KnmListPackage/MainModule/reducers/knmListReducer';
import { knmListFilterReducer } from 'KnmListPackage/KnmListFiltersModule/reducers/knmListFilterReducer';
import { pmListFilterReducer } from 'PreventionPackage/PmListFiltersModule/reducers/pmListFilterReducer';
import { knmSearchReducer } from 'KnmSearchPackage/MainModule/reducers/knmSearchReducer';
import { userRequirementsReducer } from 'PersonalAccountPackage/UserRequirementsModule/reducers/userRequirementsReducer';
import { checkListsReducer } from 'PersonalAccountPackage/CheckListModule/reducers/checkListsReducer';
import { adminReducer } from 'PersonalAccountPackage/AdminModule/reducers/adminReducer';
import { newsReducer } from 'NewsPackage/MainModule/reducers/newsReducer';
import { newsFormReducer } from 'NewsPackage/FormModule/reducers/newsFormReducer';
import { importReducer } from 'ImportPackage/MainModule/reducers/importReducer';
import { erknmAnalyticsReducer } from 'AnalyticsPackage/ErknmAnalyticsModule/reducers/erknmAnalyticsReducer';
import { erknmAnalyticsProsecutorOrganizationReducer } from 'AnalyticsPackage/ErknmAnalyticsProsecutorOrganizationModule/reducers/erknmAnalyticsProsecutorOrganizationReducer';
import { similaritiesReducer } from 'SimilaritiesPackage/MainModule/reducers/similaritiesReducer';
import { knmRegistryReducer } from 'RegistryPackage/MainModule/reducers/knmRegistryReducer';
import { knmPreviewReducer } from 'KnmPreviewPackage/MainModule/reducers/knmPreviewReducer';
import { preventionReducer } from 'PreventionPackage/MainModule/reducers/preventionReducer';
import { AuthActionsEnum } from 'AuthPackage/MainModule/enums/AuthActionsEnum';
import { authReducer } from 'AuthPackage/MainModule/reducers/authReducer';
import { knmErknmReducer } from 'KnmErknmPackage/MainModule/reducers/knmErknmReducer';
import { knmPmReducer } from 'KnmPmPackage/MainModule/reducers/knmPmReducer';
import { knmErpReducer } from 'KnmErpPackage/MainModule/reducers/knmErpReducer';
import { feedbackReducer } from 'FeedbackPackage/MainModule/reducers/feedbackReducer';
import { decisionListReducer } from 'DecisionListPackage/MainModule/reducers/decisionListReducer';
import { requirementsNpaReducer } from 'KnmPackage/RequirementsNpaModalModule/reducers/requirementsNpaReducer';
import { decisionReducer } from 'DecisionPackage/MainModule/reducers/decisionReducer';
import { userNotificationReducer } from 'UserNotificationPackage/MainModule/reducers/userNotificationReducer';
import { publishRulesReducer } from 'PublishRulesPackage/MainModule/reducers/publishRulesReducer';
import { agreementListFilterReducer } from 'AgreementListPackage/AgreementListFiltersModule/reducers/agreementListFilterReducer';
import { agreementListReducer } from 'AgreementListPackage/MainModule/reducers/agreementListReducer';
import { decisionListFilterReducer } from 'DecisionListPackage/DecisionListFiltersModule/reducers/decisionListFilterReducer';

import { rootSaga } from '../sagas/rootSaga';
import { IRootState } from '../interfaces/IRootState';
import { coreReducer } from '../reducers/coreReducer';
import { RootActions } from '../types/RootActions';
import { changeKnoInPlanReducer } from '../../../ChangeKnoInPlanPackage/reducers/changeKnoInPlanReducer';
import { passportGroupReducer } from '../../../PassportGroupPackage/MainModule/reducers/passportGroupReducer';

const sagaMiddleware = createSagaMiddleware();
const reducers = combineReducers<IRootState | undefined>({
  account: accountReducer,
  auth: authReducer,
  header: headerReducer,
  userInfo: userInfoReducer,
  analytics: analyticsReducer,
  core: coreReducer,
  catalogs: catalogsReducer,
  dictionaries: dictionariesReducer,
  userNotices: noticeReducer,
  registry: registryReducer,
  knm: knmReducer,
  planList: planListReducer,
  planListFilter: planListFilterReducer,
  plan: planReducer,
  knmListByPlan: knmListByPlanReducer,
  decisionList: decisionListReducer,
  decision: decisionReducer,
  smevMonitoring: smevMonitoringReducer,
  export: exportReducer,
  knmList: knmListReducer,
  knmListFilter: knmListFilterReducer,
  agreementListFilter: agreementListFilterReducer,
  agreementList: agreementListReducer,
  pmListFilter: pmListFilterReducer,
  decisionListFilter: decisionListFilterReducer,
  knmSearch: knmSearchReducer,
  userRequirements: userRequirementsReducer,
  checkLists: checkListsReducer,
  admin: adminReducer,
  news: newsReducer,
  newsForm: newsFormReducer,
  import: importReducer,
  erknmAnalytics: erknmAnalyticsReducer,
  erknmAnalyticsProsecutorOrganization: erknmAnalyticsProsecutorOrganizationReducer,
  similarities: similaritiesReducer,
  knmRegistry: knmRegistryReducer,
  knmPreview: knmPreviewReducer,
  prevention: preventionReducer,
  knmErknm: knmErknmReducer,
  knmPm: knmPmReducer,
  knmErp: knmErpReducer,
  feedback: feedbackReducer,
  requirementsNpa: requirementsNpaReducer,
  changeKnoInPlan: changeKnoInPlanReducer,
  userNotification: userNotificationReducer,
  publishRules: publishRulesReducer,
  passportGroup: passportGroupReducer,
});

const rootReducer = (state: IRootState, action: RootActions) => {
  if (action.type === AuthActionsEnum.CLEAR_USER) {
    return reducers(undefined, action);
  }

  return reducers(state, action);
};

export const store = createStore(rootReducer, composeWithDevTools(applyMiddleware(sagaMiddleware)));

sagaMiddleware.run(rootSaga);
